import { Suspense, useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCan } from "../context/useCan";
import { PRODUCT_NAME } from "../lib/brand";
import { Logo } from "./Logo";
import { ColorModeToggle } from "./ColorModeToggle";
import Footer from "./Footer";
import { EmailConfirmBanner } from "./EmailConfirmation";
import { Button, LoadingPage } from "./ui";
import RoleBadge from "./RoleBadge";
import { CUSTOM_WORK_OPEN, CUSTOM_WORK_VISIBLE } from "../lib/devTime";
import { clearReturnPath, clearSignedOut } from "../lib/returnTo";
import { ROLE_BLURBS, ROLE_NAMES, normalizeRole } from "../lib/roles";

type NavItem = { to: string; label: string; soon?: boolean };

// Billing is for the roles that can see it (lib/roles.ts); the rest of a team
// would only get a page saying they cannot.
function navFor(canSeeBilling: boolean): NavItem[] {
  return [
    { to: "/agents", label: "Agents" },
    ...(canSeeBilling ? [{ to: "/billing", label: "Billing" }] : []),
    { to: "/team", label: "Team" },
    ...(CUSTOM_WORK_VISIBLE ? [{ to: "/custom-work", label: "Custom work", soon: !CUSTOM_WORK_OPEN }] : [])
  ];
}

export default function Layout() {
  const { me, tenant, signOut, switchTenant } = useAuth();
  const can = useCan();
  const navigate = useNavigate();
  const NAV = navFor(can("billingRead"));

  // The dashboard is showing, so any link remembered across sign-in has been
  // followed (the redirect to it ran as this rendered). Forget it.
  useEffect(() => {
    clearReturnPath();
    clearSignedOut();
  }, []);

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <header className="border-b border-slate-200 bg-surface">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
          <NavLink to="/agents" aria-label={PRODUCT_NAME} className="flex shrink-0">
            <Logo compact />
          </NavLink>

          {/* On a phone the links get a row of their own under the logo and the
              account controls, and scroll sideways rather than wrap. */}
          <nav
            className="order-last -mx-1 flex w-full items-center gap-1 overflow-x-auto sm:order-none sm:mx-0 sm:w-auto"
            aria-label="Main"
          >
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  "whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium transition " +
                  (isActive ? "bg-brand-50 text-brand-700" : "text-slate-600 hover:bg-slate-100")
                }
              >
                {item.label}
                {/* Amber, not brand: the active tab is already brand-tinted, and
                    gray read as part of the label. */}
                {item.soon && (
                  <span className="ml-1.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                    Soon
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          {/* On a phone the account, colour mode and sign out live behind the
              avatar, so the header is the logo and the tabs, not three rows. */}
          <MobileMenu />

          <div className="ml-auto hidden items-center gap-3 sm:flex">
            {/* Someone in more than one account picks which one they are
                working in, each listed with their role there. Switching goes
                to that account's agents, since an agent page from the last
                one would not be found. Otherwise the account's name and your
                role in it, on every page, so what you can change is never a
                surprise. */}
            {tenant && me && me.tenants.length > 1 ? (
              <AccountMenu
                tenants={me.tenants}
                current={tenant.tenantId}
                onPick={(tenantId) => {
                  switchTenant(tenantId);
                  navigate("/agents");
                }}
              />
            ) : (
              tenant && (
                <>
                  <NavLink
                    to="/team"
                    className="max-w-32 truncate text-xs text-slate-500 hover:text-slate-700 sm:max-w-48"
                    title="Your account. Owners can rename it on the Team page."
                  >
                    {tenant.name}
                  </NavLink>
                  <RolePill role={tenant.role} />
                </>
              )
            )}
            {me?.isAdmin && (
              <NavLink to="/admin" className="text-xs font-medium text-slate-500 hover:text-slate-700">
                Admin
              </NavLink>
            )}
            <ColorModeToggle />
            <Avatar />
            <Button variant="ghost" className="whitespace-nowrap" onClick={() => void signOut()}>
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-8">
        <EmailConfirmBanner />
        {/* Pages load on first visit (App.tsx); the header stays meanwhile. */}
        <Suspense fallback={<LoadingPage label="Loading…" />}>
          <Outlet />
        </Suspense>
      </main>

      <Footer />
    </div>
  );
}

function RolePill({ role }: { role: string | undefined }) {
  const known = normalizeRole(role);
  const name = ROLE_NAMES[known];
  return (
    <NavLink to="/team" aria-label={`Your role: ${name}. ${ROLE_BLURBS[known]}`} title={`${name}: ${ROLE_BLURBS[known]}`}>
      <RoleBadge role={known} />
    </NavLink>
  );
}

// The account switcher: a menu rather than a <select>, because an <option>
// can only hold text and each account shows its role as a pill.
function AccountMenu({
  tenants,
  current,
  onPick
}: {
  tenants: { tenantId: string; name: string; role?: string }[];
  current: string;
  onPick: (tenantId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const active = tenants.find((t) => t.tenantId === current) || tenants[0];
  const role = normalizeRole(active.role);

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent | KeyboardEvent) => {
      if (event instanceof KeyboardEvent ? event.key === "Escape" : !box.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
    };
  }, [open]);

  return (
    <div ref={box} className="relative">
      <button
        type="button"
        aria-label={`Account: ${active.name}. Your role: ${ROLE_NAMES[role]}`}
        aria-haspopup="menu"
        aria-expanded={open}
        title={`${ROLE_NAMES[role]}: ${ROLE_BLURBS[role]}`}
        onClick={() => setOpen(!open)}
        className="flex max-w-64 items-center gap-2 rounded-lg border border-slate-200 bg-surface px-2 py-1 text-xs text-slate-700 hover:bg-slate-50"
      >
        <span className="truncate">{active.name}</span>
        <RoleBadge role={role} />
        <span aria-hidden="true" className="text-slate-400">&#9662;</span>
      </button>
      {open && (
        <ul role="menu" aria-label="Your accounts" className="absolute right-0 z-20 mt-1 w-72 overflow-hidden rounded-xl border border-slate-200 bg-surface py-1 shadow-lg">
          {tenants.map((t) => (
            <li key={t.tenantId} role="none">
              <button
                type="button"
                role="menuitemradio"
                aria-checked={t.tenantId === active.tenantId}
                onClick={() => {
                  setOpen(false);
                  if (t.tenantId !== active.tenantId) onPick(t.tenantId);
                }}
                className={
                  "flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-slate-50 " +
                  (t.tenantId === active.tenantId ? "font-medium text-slate-900" : "text-slate-700")
                }
              >
                <span className="truncate">{t.name}</span>
                <RoleBadge role={t.role} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// The signed-in person's picture, or their initial: email and password
// accounts have no photo, so there is always a fallback rather than a broken
// image.
function Avatar() {
  const { me } = useAuth();
  return me?.photoUrl ? (
    <img src={me.photoUrl} alt="" className="h-8 w-8 rounded-full" referrerPolicy="no-referrer" />
  ) : (
    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-100 text-xs font-semibold text-brand-700">
      {(me?.displayName || me?.email || "?").slice(0, 1).toUpperCase()}
    </span>
  );
}

// Phones only: the avatar opens a menu with who you are, the account you are
// in (and the others, each with your role there), colour mode and sign out.
function MobileMenu() {
  const { me, tenant, signOut, switchTenant } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent | KeyboardEvent) => {
      if (event instanceof KeyboardEvent ? event.key === "Escape" : !box.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
    };
  }, [open]);

  const accounts = me?.tenants ?? [];
  return (
    <div ref={box} className="relative ml-auto sm:hidden">
      <button
        type="button"
        aria-label="Your account menu"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 rounded-full p-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-600"
      >
        <Avatar />
        <span aria-hidden="true" className="text-xs text-slate-400">&#9662;</span>
      </button>
      {open && (
        <div role="menu" aria-label="Your account" className="absolute right-0 z-30 mt-2 w-72 rounded-xl border border-slate-200 bg-surface p-3 shadow-lg">
          <p className="truncate text-sm font-medium text-slate-900">{me?.displayName || me?.email}</p>
          {me?.displayName && <p className="truncate text-xs text-slate-500">{me.email}</p>}

          <p className="mt-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">{accounts.length > 1 ? "Accounts" : "Account"}</p>
          <ul className="mt-1 space-y-0.5">
            {accounts.map((t) => (
              <li key={t.tenantId}>
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={t.tenantId === tenant?.tenantId}
                  onClick={() => {
                    setOpen(false);
                    if (t.tenantId !== tenant?.tenantId) {
                      switchTenant(t.tenantId);
                      navigate("/agents");
                    }
                  }}
                  className={
                    "flex w-full items-center justify-between gap-3 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-slate-50 " +
                    (t.tenantId === tenant?.tenantId ? "font-medium text-slate-900" : "text-slate-700")
                  }
                >
                  <span className="truncate">{t.name}</span>
                  <RoleBadge role={t.role} />
                </button>
              </li>
            ))}
          </ul>

          {me?.isAdmin && (
            <NavLink to="/admin" onClick={() => setOpen(false)} className="mt-3 block border-t border-slate-100 pt-3 text-sm text-slate-700 hover:text-slate-900">
              Admin
            </NavLink>
          )}
          <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3">
            <span className="text-sm text-slate-700">Dark mode</span>
            <ColorModeToggle />
          </div>
          <Button variant="ghost" className="mt-2 w-full justify-start" onClick={() => void signOut()}>
            Sign out
          </Button>
        </div>
      )}
    </div>
  );
}

